import { useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import SignaturePadLib from "signature_pad";

import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";

/**
 * SignaturePad component using signature_pad library
 *
 * This component uses forwardRef because it needs to expose imperative methods
 * (clear, isEmpty, getSignature, etc.) to parent components via a ref.
 * Without forwardRef, parent components would not be able to call methods like
 * signatureRef.current.clear() to programmatically control the signature pad.
 * @param {Object} props - Component props
 * @param {string} props.width - Width of the canvas (default: "100%")
 * @param {string} props.height - Height of the canvas (default: "100px")
 * @param {string} props.label - Label for the signature pad
 * @param {function} props.onChange - Callback when signature changes
 * @param {Object} props.style - Additional styles for the canvas container
 * @param {Object} props.options - Additional options for signature_pad
 * @param {Array} props.buttons - Buttons for the signature pad
 * @param {React.Ref} ref - Forwarded ref for accessing component methods
 */
export default forwardRef(function SignaturePad(
  {
    width = "100%",
    height = "100px",
    label = "Signature",
    onChange,
    style = {},
    options = {},
  },
  ref,
) {
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);

  /**
   * Initialize signature pad
   */
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;

      // Set up high DPI canvas
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      // Get the container dimensions
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;

      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);

      // Initialize SignaturePad
      signaturePadRef.current = new SignaturePadLib(canvas, {
        backgroundColor: "rgba(172, 18, 18, 0)",
        penColor: options.penColor || "rgb(80, 72, 72)",
        ...options,
      });

      // Set up end stroke callback
      signaturePadRef.current.addEventListener("endStroke", () => {
        if (onChange) {
          const dataUrl = signaturePadRef.current.toDataURL();
          onChange(dataUrl);
        }
      });
    }

    // Cleanup
    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
      }
    };
  }, []);

  /**
   * Handle window resize
   */
  useEffect(() => {
    function handleResize() {
      if (canvasRef.current && signaturePadRef.current) {
        const canvas = canvasRef.current;
        const data = signaturePadRef.current.toData();

        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;

        const ctx = canvas.getContext("2d");
        ctx.scale(ratio, ratio);

        signaturePadRef.current.clear();
        signaturePadRef.current.fromData(data);
      }
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /**
   * Expose methods via ref using useImperativeHandle.
   * This allows parent components to call methods on the SignaturePad component
   * through a ref, providing programmatic control over the signature pad.
   *
   * Available methods:
   * - clear(): Clears all signature data from the pad
   * - isEmpty(): Returns true if no signature has been drawn
   * - getSignature(type): Returns the signature as a base64 data URL
   * - toData(): Returns the raw signature data as point groups
   * - fromData(data): Loads signature from point group data
   * - fromDataURL(dataUrl): Loads signature from a base64 data URL
   */
  useImperativeHandle(ref, () => ({
    /**
     * Clear the signature pad
     */
    clear: () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    },
    /**
     * Check if the signature pad is empty
     * @returns {boolean}
     */
    isEmpty: () => {
      return signaturePadRef.current ? signaturePadRef.current.isEmpty() : true;
    },
    /**
     * Get the signature as a data URL (base64)
     * @param {string} type - Image type (default: "image/png")
     * @returns {string}
     */
    getSignature: () => {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        return signaturePadRef.current.toDataURL("image/svg+xml");
      }
      return null;
    },
    /**
     * Get the signature as an SVG string
     * @returns {string|null} SVG string or null if empty
     */
    getSignatureSVG: () => {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        return signaturePadRef.current.toSVG();
      }
      return null;
    },
    /**
     * Get the signature pad data as point groups
     * @returns {Array}
     */
    toData: () => {
      return signaturePadRef.current ? signaturePadRef.current.toData() : [];
    },
    /**
     * Load signature data from point groups
     * @param {Array} data - Point group data
     */
    fromData: (data) => {
      if (signaturePadRef.current && data) {
        signaturePadRef.current.fromData(data);
      }
    },
    /**
     * Load signature from data URL
     * @param {string} dataUrl - Base64 data URL
     */
    fromDataURL: (dataUrl) => {
      if (signaturePadRef.current && dataUrl) {
        signaturePadRef.current.fromDataURL(dataUrl);
      }
    },
  }));

  return (
    <MUIBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <MUITypography variant="caption" color="text.secondary">
        {label}
      </MUITypography>
      <canvas
        ref={canvasRef}
        style={{
          width: width,
          height: height,
          touchAction: "none",
          ...style,
        }}
      />
    </MUIBox>
  );
});
